/**
 * Training Data Layer Strategy Implementation
 *
 * Implements layer-specific logic for Training Data (T) layer processing.
 * Training Data layer handles various file formats for AI training datasets.
 * This layer requires component selection from other layers.
 */

import {
  BaseLayerStrategy,
  LayerFieldConfig,
  ValidationResult,
} from './LayerStrategy';

export interface TrainingDataLayerMetadata {
  layerType: 'training';

  // Core training data information
  datasetName?: string;
  datasetType?:
    | 'Image_Classification'
    | 'Video_Analysis'
    | 'Audio_Processing'
    | 'Multi_Modal'
    | 'Behavioral'
    | 'Style_Transfer';
  description?: string;

  // Dataset characteristics
  dataFormat?: string[]; // JSON, CSV, Images, Videos, etc.
  dataSize?: number; // in MB
  sampleCount?: number; // number of samples/examples
  labelType?:
    | 'Supervised'
    | 'Unsupervised'
    | 'Semi_Supervised'
    | 'Reinforcement';

  // Training metadata
  purpose?: string; // what this dataset trains for
  targetModel?: string; // intended AI model type
  qualityScore?: number; // 0-1 dataset quality rating
  annotationLevel?: 'Raw' | 'Basic' | 'Detailed' | 'Expert';

  // Component assets (NNA addresses from other layers)
  sourceAssets?: {
    songAssets?: string[]; // G layer assets used as training data
    starAssets?: string[]; // S layer assets used as training data
    lookAssets?: string[]; // L layer assets used as training data
    moveAssets?: string[]; // M layer assets used as training data
    worldAssets?: string[]; // W layer assets used as training data
  };

  // Data structure
  features?: string[]; // what features are captured
  labels?: string[]; // what labels/categories exist
  metadata?: string[]; // what metadata fields are included
  preprocessing?: string[]; // preprocessing steps applied

  // Validation and testing
  splitRatio?: {
    training?: number; // 0-1
    validation?: number; // 0-1
    testing?: number; // 0-1
  };
  crossValidation?: boolean;
  benchmarkResults?: any;

  // AI training context
  difficulty?: 'Easy' | 'Medium' | 'Hard' | 'Expert';
  domain?: string[]; // application domains
  biasChecked?: boolean;
  ethicalReview?: boolean;

  // Usage and performance
  downloadCount?: number;
  citationCount?: number;
  accuracy?: number; // 0-1 if benchmarked
  lastUpdated?: Date;

  // Technical specifications
  version?: string;
  license?: string;
  creator?: string;
  institution?: string;
}

export class TrainingDataLayerStrategy extends BaseLayerStrategy {
  readonly layerCode = 'T';
  readonly layerName = 'Training Data';
  readonly layerDescription =
    'AI training datasets aggregated from multiple layer components';

  getRequiredFields(): string[] {
    return ['datasetName', 'datasetType', 'purpose'];
  }

  getOptionalFields(): string[] {
    return [
      'description',
      'dataFormat',
      'dataSize',
      'sampleCount',
      'labelType',
      'targetModel',
      'qualityScore',
      'annotationLevel',
      'sourceAssets',
      'features',
      'labels',
      'metadata',
      'preprocessing',
      'splitRatio',
      'crossValidation',
      'benchmarkResults',
      'difficulty',
      'domain',
      'biasChecked',
      'ethicalReview',
      'downloadCount',
      'citationCount',
      'accuracy',
      'lastUpdated',
      'version',
      'license',
      'creator',
      'institution',
    ];
  }

  getFieldConfig(): LayerFieldConfig {
    return {
      required: this.getRequiredFields(),
      optional: this.getOptionalFields(),
      computed: ['dataSize', 'sampleCount', 'qualityScore', 'splitRatio'],
      inherited: ['domain', 'features'],
    };
  }

  extractMetadataFromAI(aiResponse: any): TrainingDataLayerMetadata {
    const layerMetadata =
      aiResponse.layerMetadata || aiResponse.trainingMetadata || {};

    return {
      layerType: 'training',

      // Core information
      datasetName: layerMetadata.datasetName || aiResponse.datasetName || '',
      datasetType: this.normalizeDatasetType(
        layerMetadata.datasetType || aiResponse.datasetType
      ),
      description: layerMetadata.description || aiResponse.description || '',

      // Dataset characteristics
      dataFormat: this.normalizeArray(
        layerMetadata.dataFormat || aiResponse.dataFormat
      ),
      dataSize: layerMetadata.dataSize || aiResponse.dataSize,
      sampleCount: layerMetadata.sampleCount || aiResponse.sampleCount,
      labelType: this.normalizeLabelType(
        layerMetadata.labelType || aiResponse.labelType
      ),

      // Training metadata
      purpose: layerMetadata.purpose || aiResponse.purpose,
      targetModel: layerMetadata.targetModel || aiResponse.targetModel,
      qualityScore: layerMetadata.qualityScore || aiResponse.qualityScore,
      annotationLevel: this.normalizeAnnotationLevel(
        layerMetadata.annotationLevel || aiResponse.annotationLevel
      ),

      // Component assets
      sourceAssets: this.extractSourceAssets(
        layerMetadata.sourceAssets || aiResponse.sourceAssets
      ),

      // Data structure
      features: this.normalizeArray(
        layerMetadata.features || aiResponse.features
      ),
      labels: this.normalizeArray(layerMetadata.labels || aiResponse.labels),
      metadata: this.normalizeArray(
        layerMetadata.metadata || aiResponse.metadata
      ),
      preprocessing: this.normalizeArray(
        layerMetadata.preprocessing || aiResponse.preprocessing
      ),

      // Validation
      splitRatio: this.normalizeSplitRatio(
        layerMetadata.splitRatio || aiResponse.splitRatio
      ),
      crossValidation:
        layerMetadata.crossValidation || aiResponse.crossValidation,
      benchmarkResults:
        layerMetadata.benchmarkResults || aiResponse.benchmarkResults,

      // AI context
      difficulty: this.normalizeDifficulty(
        layerMetadata.difficulty || aiResponse.difficulty
      ),
      domain: this.normalizeArray(layerMetadata.domain || aiResponse.domain),
      biasChecked: layerMetadata.biasChecked || aiResponse.biasChecked,
      ethicalReview: layerMetadata.ethicalReview || aiResponse.ethicalReview,

      // Technical
      version: layerMetadata.version || aiResponse.version,
      license: layerMetadata.license || aiResponse.license,
      creator: layerMetadata.creator || aiResponse.creator,
      institution: layerMetadata.institution || aiResponse.institution,
      accuracy: layerMetadata.accuracy || aiResponse.accuracy,
    };
  }

  enhanceWithDefaults(
    metadata: TrainingDataLayerMetadata
  ): TrainingDataLayerMetadata {
    return {
      ...this.getDefaultMetadata(),
      ...metadata,
      layerType: 'training',

      // Ensure required fields have defaults
      datasetName: metadata.datasetName || 'Untitled Dataset',
      datasetType: metadata.datasetType || 'Multi_Modal',
      purpose: metadata.purpose || 'General AI Training',

      // Normalize arrays
      dataFormat: this.normalizeArray(metadata.dataFormat),
      features: this.normalizeArray(metadata.features),
      labels: this.normalizeArray(metadata.labels),
      metadata: this.normalizeArray(metadata.metadata),
      preprocessing: this.normalizeArray(metadata.preprocessing),
      domain: this.normalizeArray(metadata.domain),

      // Default values
      labelType: metadata.labelType || 'Supervised',
      annotationLevel: metadata.annotationLevel || 'Basic',
      difficulty: metadata.difficulty || 'Medium',
      biasChecked: metadata.biasChecked ?? false,
      ethicalReview: metadata.ethicalReview ?? false,

      // Ensure source assets object exists
      sourceAssets: metadata.sourceAssets || {},
    };
  }

  generateTags(
    metadata: TrainingDataLayerMetadata,
    description?: string
  ): string[] {
    const tags = new Set<string>();

    // Basic layer tags
    tags.add('training-data');
    tags.add('dataset');
    tags.add('ai-training');
    tags.add('machine-learning');

    // Dataset type tags
    if (metadata.datasetType) {
      tags.add(metadata.datasetType.toLowerCase().replace(/_/g, '-'));
      switch (metadata.datasetType) {
        case 'Image_Classification':
          tags.add('computer-vision');
          tags.add('image-recognition');
          break;
        case 'Video_Analysis':
          tags.add('video-processing');
          tags.add('temporal-analysis');
          break;
        case 'Audio_Processing':
          tags.add('audio-analysis');
          tags.add('sound-processing');
          break;
        case 'Multi_Modal':
          tags.add('multimodal');
          tags.add('cross-modal');
          break;
        case 'Behavioral':
          tags.add('behavior-analysis');
          tags.add('user-modeling');
          break;
        case 'Style_Transfer':
          tags.add('style-transfer');
          tags.add('artistic-ai');
          break;
      }
    }

    // Label type tags
    if (metadata.labelType) {
      tags.add(metadata.labelType.toLowerCase().replace(/_/g, '-'));
      switch (metadata.labelType) {
        case 'Supervised':
          tags.add('labeled');
          tags.add('ground-truth');
          break;
        case 'Unsupervised':
          tags.add('unlabeled');
          tags.add('clustering');
          break;
        case 'Semi_Supervised':
          tags.add('partially-labeled');
          break;
        case 'Reinforcement':
          tags.add('rl-dataset');
          tags.add('reward-based');
          break;
      }
    }

    // Data format tags
    if (metadata.dataFormat && metadata.dataFormat.length > 0) {
      metadata.dataFormat.forEach(format => {
        tags.add(format.toLowerCase());
        tags.add(`${format.toLowerCase()}-format`);
      });
    }

    // Annotation level tags
    if (metadata.annotationLevel) {
      tags.add(`${metadata.annotationLevel.toLowerCase()}-annotation`);
      switch (metadata.annotationLevel) {
        case 'Expert':
          tags.add('high-quality');
          tags.add('expert-labeled');
          break;
        case 'Raw':
          tags.add('raw-data');
          tags.add('unprocessed');
          break;
      }
    }

    // Difficulty tags
    if (metadata.difficulty) {
      tags.add(`${metadata.difficulty.toLowerCase()}-difficulty`);
    }

    // Domain tags
    if (metadata.domain && metadata.domain.length > 0) {
      metadata.domain.forEach(d => {
        tags.add(d.toLowerCase().replace(/\s+/g, '-'));
        tags.add(`${d.toLowerCase()}-domain`);
      });
    }

    // Feature tags
    if (metadata.features && metadata.features.length > 0) {
      metadata.features.forEach(feature => {
        tags.add(feature.toLowerCase().replace(/\s+/g, '-'));
      });
    }

    // Quality tags
    if (metadata.qualityScore) {
      if (metadata.qualityScore > 0.8) {
        tags.add('high-quality');
        tags.add('premium');
      } else if (metadata.qualityScore < 0.4) {
        tags.add('low-quality');
        tags.add('basic');
      }
    }

    // Size tags
    if (metadata.sampleCount) {
      if (metadata.sampleCount > 10000) {
        tags.add('large-dataset');
        tags.add('big-data');
      } else if (metadata.sampleCount < 100) {
        tags.add('small-dataset');
        tags.add('limited-samples');
      }
    }

    // Validation tags
    if (metadata.crossValidation) {
      tags.add('cross-validated');
      tags.add('robust');
    }

    // Ethics tags
    if (metadata.biasChecked) {
      tags.add('bias-checked');
      tags.add('fair');
    }

    if (metadata.ethicalReview) {
      tags.add('ethically-reviewed');
      tags.add('responsible-ai');
    }

    // Source component tags
    if (metadata.sourceAssets) {
      if (metadata.sourceAssets.songAssets?.length) tags.add('audio-sourced');
      if (metadata.sourceAssets.starAssets?.length)
        tags.add('character-sourced');
      if (metadata.sourceAssets.lookAssets?.length) tags.add('fashion-sourced');
      if (metadata.sourceAssets.moveAssets?.length)
        tags.add('movement-sourced');
      if (metadata.sourceAssets.worldAssets?.length)
        tags.add('environment-sourced');
    }

    // License tags
    if (metadata.license) {
      tags.add(metadata.license.toLowerCase());
      if (metadata.license.toLowerCase().includes('open')) {
        tags.add('open-source');
      }
    }

    return Array.from(tags).slice(0, 30);
  }

  categorizeTags(tags: string[]): {
    primary: string[];
    secondary: string[];
    descriptive: string[];
  } {
    const categories = {
      primary: [] as string[],
      secondary: [] as string[],
      descriptive: [] as string[],
    };

    tags.forEach(tag => {
      const lowerTag = tag.toLowerCase();

      // Primary tags - type and domain
      if (
        lowerTag.includes('dataset') ||
        lowerTag.includes('training') ||
        lowerTag.includes('classification') ||
        lowerTag.includes('analysis') ||
        lowerTag.includes('processing')
      ) {
        categories.primary.push(tag);
      }
      // Secondary tags - characteristics and quality
      else if (
        lowerTag.includes('quality') ||
        lowerTag.includes('annotation') ||
        lowerTag.includes('difficulty') ||
        lowerTag.includes('supervised')
      ) {
        categories.secondary.push(tag);
      }
      // Descriptive tags - specific details
      else {
        categories.descriptive.push(tag);
      }
    });

    return categories;
  }

  validateBusinessRules(metadata: TrainingDataLayerMetadata): ValidationResult {
    const errors: any[] = [];
    const warnings: any[] = [];

    // Business rule: Dataset name should be meaningful
    if (metadata.datasetName && metadata.datasetName.trim().length < 5) {
      warnings.push({
        field: 'datasetName',
        message: 'Dataset name should be at least 5 characters long',
        suggestion: 'Provide a more descriptive dataset name',
      });
    }

    // Business rule: Should have purpose defined
    if (!metadata.purpose || metadata.purpose.trim().length < 10) {
      warnings.push({
        field: 'purpose',
        message:
          'Dataset purpose should be clearly defined (at least 10 characters)',
        suggestion: 'Explain what this dataset is intended to train',
      });
    }

    // Business rule: Should have source assets for training data
    if (metadata.sourceAssets) {
      const hasSourceAssets =
        (metadata.sourceAssets.songAssets &&
          metadata.sourceAssets.songAssets.length > 0) ||
        (metadata.sourceAssets.starAssets &&
          metadata.sourceAssets.starAssets.length > 0) ||
        (metadata.sourceAssets.lookAssets &&
          metadata.sourceAssets.lookAssets.length > 0) ||
        (metadata.sourceAssets.moveAssets &&
          metadata.sourceAssets.moveAssets.length > 0) ||
        (metadata.sourceAssets.worldAssets &&
          metadata.sourceAssets.worldAssets.length > 0);

      if (!hasSourceAssets) {
        warnings.push({
          field: 'sourceAssets',
          message:
            'Training dataset should include source assets from other layers',
          suggestion: 'Select component assets that this dataset is based on',
        });
      }
    }

    // Business rule: Split ratios should sum to approximately 1
    if (metadata.splitRatio) {
      const total =
        (metadata.splitRatio.training || 0) +
        (metadata.splitRatio.validation || 0) +
        (metadata.splitRatio.testing || 0);

      if (Math.abs(total - 1) > 0.1) {
        warnings.push({
          field: 'splitRatio',
          message: 'Split ratios should sum to approximately 1.0',
          suggestion: 'Adjust training, validation, and testing ratios',
        });
      }
    }

    // Business rule: Quality score should be 0-1
    if (
      metadata.qualityScore &&
      (metadata.qualityScore < 0 || metadata.qualityScore > 1)
    ) {
      warnings.push({
        field: 'qualityScore',
        message: 'Quality score should be between 0 and 1',
        suggestion: 'Provide a decimal value between 0 and 1',
      });
    }

    // Business rule: Sample count should be reasonable
    if (metadata.sampleCount && metadata.sampleCount < 10) {
      warnings.push({
        field: 'sampleCount',
        message:
          'Very small dataset (< 10 samples) may not be effective for training',
        suggestion: 'Consider increasing the number of training samples',
      });
    }

    // Business rule: Should have ethical review for large datasets
    if (
      metadata.sampleCount &&
      metadata.sampleCount > 1000 &&
      !metadata.ethicalReview
    ) {
      warnings.push({
        field: 'ethicalReview',
        message: 'Large datasets should undergo ethical review',
        suggestion: 'Consider ethical implications and bias checking',
      });
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
    };
  }

  getDefaultMetadata(): TrainingDataLayerMetadata {
    return {
      layerType: 'training',
      datasetName: '',
      datasetType: 'Multi_Modal',
      purpose: '',
      description: '',
      dataFormat: [],
      features: [],
      labels: [],
      metadata: [],
      preprocessing: [],
      domain: [],
      labelType: 'Supervised',
      annotationLevel: 'Basic',
      difficulty: 'Medium',
      biasChecked: false,
      ethicalReview: false,
      sourceAssets: {},
    };
  }

  getInheritanceRules(): {
    [field: string]: 'always' | 'if_empty' | 'never' | 'merge';
  } {
    return {
      // Training datasets are unique
      datasetName: 'never',
      purpose: 'never',
      sourceAssets: 'never',

      // Could inherit domain and features from components
      domain: 'merge',
      features: 'merge',
      labels: 'merge',
    };
  }

  private normalizeArray(value: any): string[] {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    if (typeof value === 'string') {
      return value
        .split(',')
        .map(item => item.trim())
        .filter(item => item.length > 0);
    }
    return [];
  }

  private normalizeDatasetType(
    value: any
  ): TrainingDataLayerMetadata['datasetType'] {
    if (!value) return 'Multi_Modal';

    const normalized = String(value).toLowerCase().replace(/[\s-]/g, '_');

    if (normalized.includes('image') || normalized.includes('classification'))
      return 'Image_Classification';
    if (normalized.includes('video') || normalized.includes('temporal'))
      return 'Video_Analysis';
    if (normalized.includes('audio') || normalized.includes('sound'))
      return 'Audio_Processing';
    if (normalized.includes('behavior') || normalized.includes('user'))
      return 'Behavioral';
    if (normalized.includes('style') || normalized.includes('transfer'))
      return 'Style_Transfer';

    return 'Multi_Modal';
  }

  private normalizeLabelType(
    value: any
  ): TrainingDataLayerMetadata['labelType'] {
    if (!value) return 'Supervised';

    const normalized = String(value).toLowerCase().replace(/[\s-]/g, '_');

    if (normalized.includes('unsupervised')) return 'Unsupervised';
    if (normalized.includes('semi')) return 'Semi_Supervised';
    if (normalized.includes('reinforcement') || normalized.includes('rl'))
      return 'Reinforcement';

    return 'Supervised';
  }

  private normalizeAnnotationLevel(
    value: any
  ): TrainingDataLayerMetadata['annotationLevel'] {
    if (!value) return 'Basic';

    const normalized = String(value).toLowerCase();

    if (normalized.includes('raw') || normalized.includes('none')) return 'Raw';
    if (normalized.includes('detailed') || normalized.includes('comprehensive'))
      return 'Detailed';
    if (normalized.includes('expert') || normalized.includes('professional'))
      return 'Expert';

    return 'Basic';
  }

  private normalizeDifficulty(
    value: any
  ): TrainingDataLayerMetadata['difficulty'] {
    if (!value) return 'Medium';

    const normalized = String(value).toLowerCase();

    if (normalized.includes('easy') || normalized.includes('simple'))
      return 'Easy';
    if (normalized.includes('hard') || normalized.includes('difficult'))
      return 'Hard';
    if (normalized.includes('expert') || normalized.includes('advanced'))
      return 'Expert';

    return 'Medium';
  }

  private normalizeSplitRatio(
    value: any
  ): TrainingDataLayerMetadata['splitRatio'] {
    if (!value) return undefined;

    if (typeof value === 'object') {
      return {
        training: value.training || value.train,
        validation: value.validation || value.val || value.dev,
        testing: value.testing || value.test,
      };
    }

    return undefined;
  }

  private extractSourceAssets(
    assets: any
  ): TrainingDataLayerMetadata['sourceAssets'] {
    if (!assets) return {};

    return {
      songAssets: this.normalizeArray(
        assets.songAssets || assets.songs || assets.G
      ),
      starAssets: this.normalizeArray(
        assets.starAssets || assets.stars || assets.S
      ),
      lookAssets: this.normalizeArray(
        assets.lookAssets || assets.looks || assets.L
      ),
      moveAssets: this.normalizeArray(
        assets.moveAssets || assets.moves || assets.M
      ),
      worldAssets: this.normalizeArray(
        assets.worldAssets || assets.worlds || assets.W
      ),
    };
  }
}

// Export singleton instance
export const trainingDataLayerStrategy = new TrainingDataLayerStrategy();
